import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaMethod, setMfaMethod] = useState(null);
  const [mfaToken, setMfaToken] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('authUser');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      verifyToken(savedToken);
    }
    setLoading(false);
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        // Token invalid, clear session
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
    }
  };

  const register = async (email, username, password) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, username, password }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Échec de l\'inscription');
    }

    return data;
  };

  const login = async (email, password) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Échec de la connexion');
    }

    if (data.requires_mfa) {
      setMfaRequired(true);
      setMfaMethod(data.mfa_method);
      setMfaToken(data.access_token);
      setPendingEmail(email);
      return { requiresMfa: true, method: data.mfa_method };
    }

    // Login successful
    setToken(data.access_token);
    setUser(data.user);
    localStorage.setItem('authToken', data.access_token);
    localStorage.setItem('authUser', JSON.stringify(data.user));
    
    return { success: true, user: data.user };
  };

  const verifyMfaCode = async (code) => {
    if (!pendingEmail || !mfaToken) {
      throw new Error('Session MFA invalide');
    }

    const response = await fetch(`${BACKEND_URL}/api/auth/login/mfa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: pendingEmail, code, mfa_token: mfaToken }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Code de vérification invalide');
    }

    // MFA successful
    setMfaRequired(false);
    setMfaMethod(null);
    setMfaToken(null);
    setPendingEmail(null);
    setToken(data.access_token);
    setUser(data.user);
    localStorage.setItem('authToken', data.access_token);
    localStorage.setItem('authUser', JSON.stringify(data.user));
    
    return { success: true, user: data.user };
  };

  const cancelMfa = () => {
    setMfaRequired(false);
    setMfaMethod(null);
    setMfaToken(null);
    setPendingEmail(null);
  };

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setMfaRequired(false);
    setMfaMethod(null);
    setMfaToken(null);
    setPendingEmail(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  }, []);

  const verifyEmail = async (verificationToken) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: verificationToken }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Échec de la vérification email');
    }

    return data;
  };

  const resendVerification = async (email) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Échec de l\'envoi');
    }

    return data;
  };

  const forgotPassword = async (email) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Échec de la demande');
    }

    return data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, new_password: newPassword }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Échec de la réinitialisation');
    }

    return data;
  };

  const getSecuritySettings = async () => {
    const response = await fetch(`${BACKEND_URL}/api/auth/security`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Erreur de chargement');
    }

    return data;
  };

  const setupMfa = async (method) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/mfa/setup`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ method }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Échec de la configuration MFA');
    }

    return data;
  };

  const verifyMfaSetup = async (code) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/mfa/verify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Code invalide');
    }

    return data;
  };

  const disableMfa = async (password, code) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/mfa/disable`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password, code }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Échec de la désactivation');
    }

    return data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Échec du changement de mot de passe');
    }

    return data;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    mfaRequired,
    mfaMethod,
    pendingEmail,
    register,
    login,
    verifyMfaCode,
    cancelMfa,
    logout,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    getSecuritySettings,
    setupMfa,
    verifyMfaSetup,
    disableMfa,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
